import React, { useState, useEffect, useRef } from "react";
import { captureSubmissionError } from "../../utils/sentryUtils";
import SubmittingCard from "../../components/SubmittingCard";
import SubmissionErrorCard from "../../components/SubmissionErrorCard";

const SourceOfFundsSubmitted = ({ sourceOfFundsFlowState: flowState, onFlowComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref guard prevents StrictMode double-mount from firing a second request
  const hasInitiated = useRef(false);

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;

    if (
      flowState.savings == null &&
      flowState.mortgage == null &&
      flowState.linkedSale == null &&
      flowState.remortgage == null &&
      flowState.helpToBuyIsa == null &&
      flowState.lifetimeIsa == null &&
      flowState.giftingMoney == null &&
      flowState.lendingMoney == null &&
      flowState.bonusFromEmployer == null &&
      flowState.inheritance == null &&
      flowState.trust == null
    ) {
      setError("No source of funds provided");
      setLoading(false);
      return;
    }

    fetch("/api/sourceOfFunds", {
      method: "PUT",
      body: JSON.stringify({
        formRequestId: flowState.formRequestId,

        howIsBuyerPaying: flowState.howIsBuyerPaying,

        savings: flowState.savings,
        savingsAccount1: flowState.savingsAccount1,
        savingsAccount2: flowState.savingsAccount2,
        savingsAccount3: flowState.savingsAccount3,
        savingsAccount4: flowState.savingsAccount4,
        savingsAccount5: flowState.savingsAccount5,
        savingsAccount6: flowState.savingsAccount6,
        savingsAccount7: flowState.savingsAccount7,
        savingsAccount8: flowState.savingsAccount8,
        savingsAccount9: flowState.savingsAccount9,
        savingsAccount10: flowState.savingsAccount10,
        savingsAccount11: flowState.savingsAccount11,
        savingsAccount12: flowState.savingsAccount12,

        mortgage: flowState.mortgage,
        occupiers: flowState.occupiers,

        linkedSale: flowState.linkedSale,
        remortgage: flowState.remortgage,

        numberOfHelpToBuyIsaAccounts: flowState.helpToBuyIsa,
        helpToBuyIsa1: flowState.helpToBuyIsaAccount1,
        helpToBuyIsa2: flowState.helpToBuyIsaAccount2,
        helpToBuyIsa3: flowState.helpToBuyIsaAccount3,
        helpToBuyIsa4: flowState.helpToBuyIsaAccount4,

        numberOfLifetimeIsaAccounts: flowState.lifetimeIsa,
        lifetimeIsa1: flowState.lifetimeIsaAccount1,
        lifetimeIsa2: flowState.lifetimeIsaAccount2,
        lifetimeIsa3: flowState.lifetimeIsaAccount3,
        lifetimeIsa4: flowState.lifetimeIsaAccount4,

        numberOfGifts: flowState.giftingMoney,
        giftingMoney1: flowState.giftingMoney1,
        giftingMoney2: flowState.giftingMoney2,
        giftingMoney3: flowState.giftingMoney3,
        giftingMoney4: flowState.giftingMoney4,

        numberOfLoans: flowState.lendingMoney,
        lendingMoney1: flowState.lendingMoney1,
        lendingMoney2: flowState.lendingMoney2,

        bonusFromEmployer: flowState.bonusFromEmployer,

        trust: flowState.trust,
        inheritance: flowState.inheritance,
      }),
    })
      .then(
        async (resp) => {
          if (resp.ok) {
            setLoading(false);
            onFlowComplete();
          } else {
            const body = await resp.text().catch(() => "");
            console.error("Source of funds submission failed:", resp.status, body);
            captureSubmissionError("SourceOfFunds", resp.status, body);
            setError("Unable to process your details");
            setLoading(false);
          }
        },
        (err) => {
          console.error("Source of funds submission failed:", err);
          setError("Unable to process your details");
          setLoading(false);
        },
      )
      .catch((err) => {
        console.error("Source of funds submission failed:", err);
        setError("Unable to process your details");
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- submit once on mount; ref guard prevents StrictMode re-fire

  if (loading) {
    return <SubmittingCard />;
  }

  if (error) {
    return <SubmissionErrorCard error={error} />;
  }

  return null;
};

export default SourceOfFundsSubmitted;
